import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Clock, MapPin, CheckCircle, XCircle, Play, Square, 
  Calendar, Home, User, AlertTriangle, Navigation,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const SHIFT_STATUS_COLORS = {
  OPEN: 'bg-yellow-100 text-yellow-700',
  ASSIGNED: 'bg-blue-100 text-blue-700',
  CONFIRMED: 'bg-indigo-100 text-indigo-700',
  IN_PROGRESS: 'bg-green-100 text-green-700',
  COMPLETED: 'bg-gray-100 text-gray-700',
  CANCELLED: 'bg-red-100 text-red-700',
};

export default function MyShiftsPage() {
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    from: new Date().toISOString().split('T')[0],
    to: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  });
  const [statusFilter, setStatusFilter] = useState('');
  
  // Check-in/out dialogs
  const [showCheckInDialog, setShowCheckInDialog] = useState(false);
  const [showCheckOutDialog, setShowCheckOutDialog] = useState(false);
  const [selectedShift, setSelectedShift] = useState(null);
  const [checkInNotes, setCheckInNotes] = useState('');
  const [checkOutForm, setCheckOutForm] = useState({
    notes: '',
    tasks_completed: '',
    incidents_reported: false,
    handover_notes: ''
  });
  const [location, setLocation] = useState({ lat: null, lng: null, loading: false, error: null });
  const [processing, setProcessing] = useState(false);

  const fetchShifts = useCallback(async () => {
    setLoading(true);
    try {
      // Using the original roster my-shifts endpoint
      const response = await api.get('/roster/my-shifts', {
        params: {
          page_size: 50
        }
      });
      let allShifts = response.data.items || response.data.data || [];
      
      // Filter by date range if specified
      if (dateRange.from) {
        allShifts = allShifts.filter(s => s.date >= dateRange.from);
      }
      if (dateRange.to) {
        allShifts = allShifts.filter(s => s.date <= dateRange.to);
      }
      if (statusFilter) {
        allShifts = allShifts.filter(s => s.status === statusFilter);
      }
      
      // Sort by date
      allShifts.sort((a, b) => a.date.localeCompare(b.date));
      
      // Add check-in status fields
      allShifts = allShifts.map(shift => ({
        ...shift,
        is_checked_in: !!shift.check_in_time && !shift.check_out_time,
        is_completed: !!shift.check_out_time,
        my_check_in: shift.check_in_time ? {
          check_in_time: shift.check_in_time,
          check_out_time: shift.check_out_time,
          actual_hours: shift.actual_hours
        } : null
      }));
      
      setShifts(allShifts);
    } catch (error) {
      toast.error('Failed to load shifts');
    } finally {
      setLoading(false);
    }
  }, [dateRange, statusFilter]);

  useEffect(() => {
    fetchShifts();
  }, [fetchShifts]);

  const getCurrentLocation = () => {
    setLocation({ ...location, loading: true, error: null });
    
    if (!navigator.geolocation) {
      setLocation({ lat: null, lng: null, loading: false, error: 'Geolocation not supported' });
      return;
    }
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          loading: false,
          error: null
        });
      },
      (error) => {
        setLocation({ lat: null, lng: null, loading: false, error: error.message });
        toast.warning('Could not get your location. You can still check in.');
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const openCheckInDialog = (shift) => {
    setSelectedShift(shift);
    setCheckInNotes('');
    setLocation({ lat: null, lng: null, loading: false, error: null });
    setShowCheckInDialog(true);
    // Auto-get location
    getCurrentLocation();
  };

  const openCheckOutDialog = (shift) => {
    setSelectedShift(shift);
    setCheckOutForm({ notes: '', tasks_completed: '', incidents_reported: false, handover_notes: '' });
    setLocation({ lat: null, lng: null, loading: false, error: null });
    setShowCheckOutDialog(true);
    // Auto-get location
    getCurrentLocation();
  };

  const handleCheckIn = async () => {
    if (!selectedShift) return;
    setProcessing(true);
    
    try {
      // Uses the original roster check-in API
      await api.post(`/roster/shifts/${selectedShift.id}/check-in`, {
        gpsLocation: location.lat ? { lat: location.lat, lng: location.lng } : null,
        notes: checkInNotes,
        deviceId: navigator.userAgent
      });
      toast.success('Checked in successfully!');
      setShowCheckInDialog(false);
      fetchShifts();
    } catch (error) {
      toast.error(error.response?.data?.detail?.message || error.response?.data?.detail || 'Failed to check in');
    } finally {
      setProcessing(false);
    }
  };

  const handleCheckOut = async () => {
    if (!selectedShift) return;
    setProcessing(true);
    
    try {
      // Uses the original roster check-out API
      const response = await api.post(`/roster/shifts/${selectedShift.id}/check-out`, {
        gpsLocation: location.lat ? { lat: location.lat, lng: location.lng } : null,
        notes: checkOutForm.notes,
        handoverNotes: checkOutForm.handover_notes,
        tasks_completed: checkOutForm.tasks_completed,
        incidents_reported: checkOutForm.incidents_reported
      });
      const actualHours = response.data?.data?.actual_hours || 
                         ((new Date(response.data?.data?.check_out_time) - new Date(response.data?.data?.check_in_time)) / 3600000).toFixed(2);
      toast.success(`Checked out! Hours worked: ${actualHours}`);
      setShowCheckOutDialog(false);
      fetchShifts();
    } catch (error) {
      toast.error(error.response?.data?.detail?.message || error.response?.data?.detail || 'Failed to check out');
    } finally {
      setProcessing(false);
    }
  };

  const getShiftTimeDisplay = (shift) => {
    const start = shift.start_time;
    const end = shift.end_time;
    return `${start} - ${end}`;
  };

  const isToday = (dateStr) => {
    return dateStr === new Date().toISOString().split('T')[0];
  };

  const canCheckIn = (shift) => {
    return !shift.is_checked_in && !shift.is_completed && 
           ['ASSIGNED', 'CONFIRMED', 'PARTIALLY_FILLED'].includes(shift.status);
  };

  const canCheckOut = (shift) => {
    return shift.is_checked_in && !shift.is_completed;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">My Shifts</h1>
        <p className="text-muted-foreground mt-1">View and manage your assigned shifts</p>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div>
              <Label className="text-xs">From Date</Label>
              <Input
                type="date"
                value={dateRange.from}
                onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
                className="w-40"
              />
            </div>
            <div>
              <Label className="text-xs">To Date</Label>
              <Input
                type="date"
                value={dateRange.to}
                onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
                className="w-40"
              />
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={statusFilter || 'ALL'} onValueChange={(v) => setStatusFilter(v === 'ALL' ? '' : v)}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Statuses</SelectItem>
                  <SelectItem value="ASSIGNED">Assigned</SelectItem>
                  <SelectItem value="CONFIRMED">Confirmed</SelectItem>
                  <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                  <SelectItem value="COMPLETED">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" onClick={fetchShifts}>Refresh</Button>
          </div>
        </CardContent>
      </Card>

      {/* Shifts List */}
      {shifts.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No shifts found for the selected date range</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {shifts.map((shift) => (
            <Card 
              key={shift.id} 
              className={`${isToday(shift.date) ? 'border-primary border-2' : ''} ${shift.is_checked_in ? 'bg-green-50/50' : ''}`}
              data-testid={`shift-card-${shift.id}`}
            >
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-base">{shift.title || shift.shift_type}</CardTitle>
                    <CardDescription className="flex items-center gap-1 mt-1">
                      <Calendar className="h-3 w-3" />
                      {new Date(shift.date).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })}
                      {isToday(shift.date) && <Badge className="ml-2 bg-primary text-primary-foreground text-xs">Today</Badge>}
                    </CardDescription>
                  </div>
                  <Badge className={SHIFT_STATUS_COLORS[shift.status] || 'bg-gray-100'}>
                    {shift.status}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">{getShiftTimeDisplay(shift)}</span>
                </div>
                
                {shift.house_name && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Home className="h-4 w-4" />
                    {shift.house_name}
                  </div>
                )}
                
                {shift.service_name && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <User className="h-4 w-4" />
                    {shift.service_name}
                  </div>
                )}

                {/* Check-in Status */}
                {shift.my_check_in && (
                  <div className="p-2 rounded bg-muted/50 text-xs space-y-1">
                    <div className="flex items-center gap-1 text-green-600">
                      <CheckCircle className="h-3 w-3" />
                      Checked in: {new Date(shift.my_check_in.check_in_time).toLocaleTimeString()}
                    </div>
                    {shift.my_check_in.check_out_time && (
                      <div className="flex items-center gap-1 text-blue-600">
                        <Square className="h-3 w-3" />
                        Checked out: {new Date(shift.my_check_in.check_out_time).toLocaleTimeString()}
                        <span className="ml-2">({shift.my_check_in.actual_hours}h)</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-2 pt-2">
                  {canCheckIn(shift) && (
                    <Button 
                      size="sm" 
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      onClick={() => openCheckInDialog(shift)}
                      data-testid={`check-in-btn-${shift.id}`}
                    >
                      <Play className="h-4 w-4 mr-1" /> Check In
                    </Button>
                  )}
                  {canCheckOut(shift) && (
                    <Button 
                      size="sm" 
                      variant="destructive"
                      className="flex-1"
                      onClick={() => openCheckOutDialog(shift)}
                      data-testid={`check-out-btn-${shift.id}`}
                    >
                      <Square className="h-4 w-4 mr-1" /> Check Out
                    </Button>
                  )}
                  {shift.is_completed && (
                    <Badge variant="outline" className="flex-1 justify-center py-2">
                      <CheckCircle className="h-4 w-4 mr-1" /> Completed
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Check-In Dialog */}
      <Dialog open={showCheckInDialog} onOpenChange={setShowCheckInDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Check In to Shift</DialogTitle>
            <DialogDescription>
              {selectedShift?.title || selectedShift?.shift_type} at {selectedShift?.house_name || 'N/A'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Location Status */}
            <div className="p-3 rounded-lg bg-muted/50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MapPin className={`h-4 w-4 ${location.lat ? 'text-green-600' : 'text-muted-foreground'}`} />
                  <span className="text-sm font-medium">GPS Location</span>
                </div>
                <Button variant="outline" size="sm" onClick={getCurrentLocation} disabled={location.loading}>
                  {location.loading ? (
                    <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Getting...</>
                  ) : (
                    <><Navigation className="h-3 w-3 mr-1" /> Refresh</>
                  )}
                </Button>
              </div>
              {location.lat && (
                <p className="text-xs text-green-600 mt-1">
                  Location captured: {location.lat.toFixed(6)}, {location.lng.toFixed(6)}
                </p>
              )}
              {location.error && (
                <p className="text-xs text-amber-600 mt-1">{location.error}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea
                value={checkInNotes}
                onChange={(e) => setCheckInNotes(e.target.value)}
                placeholder="Any notes about starting your shift..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCheckInDialog(false)}>Cancel</Button>
            <Button onClick={handleCheckIn} disabled={processing} className="bg-green-600 hover:bg-green-700">
              {processing ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Processing...</> : 'Check In Now'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Check-Out Dialog */}
      <Dialog open={showCheckOutDialog} onOpenChange={setShowCheckOutDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Check Out from Shift</DialogTitle>
            <DialogDescription>
              Complete your shift at {selectedShift?.house_name || 'N/A'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Location Status */}
            <div className="p-3 rounded-lg bg-muted/50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MapPin className={`h-4 w-4 ${location.lat ? 'text-green-600' : 'text-muted-foreground'}`} />
                  <span className="text-sm font-medium">GPS Location</span>
                </div>
                <Button variant="outline" size="sm" onClick={getCurrentLocation} disabled={location.loading}>
                  {location.loading ? (
                    <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Getting...</>
                  ) : (
                    <><Navigation className="h-3 w-3 mr-1" /> Refresh</>
                  )}
                </Button>
              </div>
              {location.lat && (
                <p className="text-xs text-green-600 mt-1">
                  Location captured: {location.lat.toFixed(6)}, {location.lng.toFixed(6)}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Tasks Completed</Label>
              <Textarea
                value={checkOutForm.tasks_completed}
                onChange={(e) => setCheckOutForm({...checkOutForm, tasks_completed: e.target.value})}
                placeholder="Summary of tasks completed during this shift..."
                rows={2}
              />
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="incidents"
                checked={checkOutForm.incidents_reported}
                onCheckedChange={(checked) => setCheckOutForm({...checkOutForm, incidents_reported: checked})}
              />
              <Label htmlFor="incidents" className="text-sm flex items-center gap-1">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                Incidents to report?
              </Label>
            </div>

            <div className="space-y-2">
              <Label>Handover Notes</Label>
              <Textarea
                value={checkOutForm.handover_notes}
                onChange={(e) => setCheckOutForm({...checkOutForm, handover_notes: e.target.value})}
                placeholder="Notes for the next shift..."
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label>Additional Notes</Label>
              <Textarea
                value={checkOutForm.notes}
                onChange={(e) => setCheckOutForm({...checkOutForm, notes: e.target.value})}
                placeholder="Any other notes..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCheckOutDialog(false)}>Cancel</Button>
            <Button onClick={handleCheckOut} disabled={processing} variant="destructive">
              {processing ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Processing...</> : 'Check Out Now'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
